import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import Layout from '../../components/Layout'
import Spinner from '../../components/Spinner'
import { getOrder, confirmPayment, rejectPayment } from '../../api/orders'
import { getSettings } from '../../api/settings'
import {
  formatMoney, formatDate, formatDateTime,
  paymentStatusLabel, paymentStatusColor,
} from '../../utils/format'
import { useAuth } from '../../context/AuthContext'
import { useTelegram } from '../../hooks/useTelegram'
import { Payment } from '../../types'

const METHOD_LABEL: Record<string, string> = { cash: '💵 Наличные', transfer: '🏦 Перевод' }

export default function PaymentDetailPage() {
  const { orderId, paymentId } = useParams<{ orderId: string; paymentId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { tg } = useTelegram()
  const qc = useQueryClient()
  const [rejectNote, setRejectNote] = useState('')
  const [showReject, setShowReject] = useState(false)
  const [imgOpen, setImgOpen] = useState(false)
  const [confirmedAmountStr, setConfirmedAmountStr] = useState('')

  useEffect(() => {
    tg?.BackButton.show()
    const cb = () => navigate(-1)
    tg?.BackButton.onClick(cb)
    return () => { tg?.BackButton.offClick(cb); tg?.BackButton.hide() }
  }, []) // eslint-disable-line

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrder(Number(orderId)),
    enabled: !!orderId,
  })
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings })

  const payment = order?.payments?.find(p => p.id === Number(paymentId))
  const rate = settings?.usd_rate ?? 12000

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['awaiting-payments'] })
    qc.invalidateQueries({ queryKey: ['order', orderId] })
  }

  const confirmedAmount = confirmedAmountStr ? Number(confirmedAmountStr.replace(/\s/g, '').replace(',', '.')) : undefined

  const confirmMut = useMutation({
    mutationFn: () => confirmPayment(Number(orderId), Number(paymentId), undefined, confirmedAmount),
    onSuccess: () => {
      tg?.HapticFeedback.notificationOccurred('success')
      toast.success('Платёж подтверждён')
      invalidate()
      navigate(-1)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Ошибка'),
  })

  const rejectMut = useMutation({
    mutationFn: () => rejectPayment(Number(orderId), Number(paymentId), rejectNote || undefined),
    onSuccess: () => {
      tg?.HapticFeedback.notificationOccurred('warning')
      toast.success('Платёж отклонён')
      invalidate()
      navigate(-1)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Ошибка'),
  })

  if (isLoading) return <Spinner className="h-screen" />
  if (!order || !payment) return (
    <Layout hideNav>
      <p className="text-center text-tg-hint mt-20">Платёж не найден</p>
    </Layout>
  )

  const lang = user?.language ?? 'ru'
  const productName = order.product ? (lang === 'uz' ? order.product.name_uz : order.product.name_ru) : '—'

  const sortedPayments = [...(order.payments ?? [])].sort((a, b) => a.payment_num - b.payment_num)

  // Payments that are unpaid (need money), sorted by month
  const unpaidFromCurrent = sortedPayments.filter(
    p => ['pending', 'overdue', 'awaiting_confirmation', 'partially_paid'].includes(p.status)
  ).sort((a, b) => a.payment_num - b.payment_num)

  const prevUnpaid = sortedPayments.filter(
    p => p.payment_num < payment.payment_num &&
      ['pending', 'overdue', 'partially_paid'].includes(p.status)
  )

  // claimed_amount = what client sent this round (clean, no ambiguity)
  // paid_amount = what admin confirmed in previous rounds (0 for fresh payments)
  const clientClaim = payment.claimed_amount   // informational display
  const priorConfirmed = payment.paid_amount   // already confirmed by admin
  const stillNeeded = payment.amount - priorConfirmed

  // Coverage: how much of client's claim covers which months
  function calcCoverage(usd: number) {
    const targets = unpaidFromCurrent.map(p => ({
      ...p,
      needed: p.amount - p.paid_amount,  // for partial months, use remaining
    }))
    let remaining = usd
    const fullyPaid: typeof targets = []
    let partial: { p: typeof targets[0]; paid: number; left: number } | null = null
    for (const p of targets) {
      if (remaining <= 0) break
      if (remaining >= p.needed) {
        fullyPaid.push(p)
        remaining -= p.needed
      } else {
        partial = { p, paid: remaining, left: p.needed - remaining }
        remaining = 0
      }
    }
    return { fullyPaid, partial, leftover: remaining }
  }

  const coverage = clientClaim > 0 ? calcCoverage(clientClaim) : null

  // Receipt image URL
  const receiptUrl = payment.receipt_file_id
    ? (payment.receipt_file_id.startsWith('http') ? payment.receipt_file_id : `/uploads/${payment.receipt_file_id}`)
    : null

  return (
    <Layout hideNav>
      <button
        onClick={() => navigate(-1)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#A8A8AE', marginBottom: 16, fontSize: 13, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        Назад
      </button>

      {/* ── Client info ── */}
      {order.user && (
        <div className="card mb-3">
          <p className="section-title mb-2">Клиент</p>
          <p className="font-semibold text-tg-text">{order.user.full_name}</p>
          <p className="text-sm text-tg-hint">{order.user.phone}</p>
          <div className="flex gap-2 mt-1.5 text-xs">
            <span className={`px-2 py-0.5 rounded-full ${order.user.is_verified ? 'bg-tg-success-soft text-tg-success' : 'bg-tg-accent-soft text-tg-accent'}`}>
              {order.user.is_verified ? '✓ Верифицирован' : 'Не верифицирован'}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-tg-bg2 text-tg-hint">
              ID {order.user.telegram_id}
            </span>
          </div>
        </div>
      )}

      {/* ── Order summary ── */}
      <div className="card mb-3">
        <p className="section-title mb-2">Заявка</p>
        <p className="text-xs text-tg-hint mb-1">{order.registry_num}</p>
        <p className="font-medium text-tg-text text-sm mb-3">{productName}</p>
        <div className="space-y-1.5 text-sm">
          <Row label="Цена товара" value={formatMoney(order.product_price)} />
          {order.discount_pct > 0 && <Row label="Скидка" value={`${order.discount_pct}%`} />}
          <Row label="Первоначальный взнос" value={formatMoney(order.down_payment)} />
          <Row label="Срок рассрочки" value={`${order.months} мес.`} />
          <Row label="Ставка" value={`${order.interest_rate}%`} />
          <Row label="Ежемесячный платёж" value={formatMoney(order.monthly_amount)} highlight />
          <Row label="Итого к выплате" value={formatMoney(order.total_amount)} />
        </div>
      </div>

      {/* ── This payment ── */}
      <div className="card mb-3">
        <div className="flex items-start justify-between mb-3">
          <p className="section-title">Платёж №{payment.payment_num}</p>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${paymentStatusColor(payment.status)}`}>
            {paymentStatusLabel(payment.status)}
          </span>
        </div>
        <div className="space-y-1.5 text-sm">
          <Row label="Плановая сумма" value={formatMoney(payment.amount)} highlight />
          {priorConfirmed > 0 && <Row label="Уже подтверждено" value={formatMoney(priorConfirmed)} />}
          <Row label="Осталось закрыть" value={formatMoney(stillNeeded)} />
          <Row label="≈ в сумах" value={`${Math.round(stillNeeded * rate).toLocaleString('ru-RU')} сум`} />
          <Row label="Срок" value={formatDate(payment.due_date)} />
          {payment.submitted_at && <Row label="Подано" value={formatDateTime(payment.submitted_at)} />}
          {payment.method && <Row label="Способ" value={METHOD_LABEL[payment.method] ?? payment.method} />}
        </div>

        {prevUnpaid.length > 0 && (
          <div className="mt-3 px-3 py-2 rounded-xl text-xs" style={{ background: 'rgba(217,119,87,0.12)', color: '#D97757' }}>
            ⚠️ Есть более ранние неоплаченные месяцы: {prevUnpaid.map(p => `#${p.payment_num}`).join(', ')}
          </div>
        )}

        {/* Client claim + coverage prediction */}
        {clientClaim > 0 && coverage && (
          <div className="mt-3 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(249,115,22,0.2)' }}>
            <div className="px-3 py-2" style={{ background: 'rgba(249,115,22,0.08)' }}>
              <p className="text-xs font-semibold" style={{ color: '#F97316' }}>Клиент говорит что перевёл</p>
              <p className="text-base font-bold text-tg-text mt-0.5">
                ≈ {Math.round(clientClaim * rate).toLocaleString('ru-RU')} сум
                <span className="text-sm font-normal text-tg-hint ml-2">({formatMoney(clientClaim)})</span>
              </p>
            </div>
            <div className="px-3 py-2 flex flex-col gap-1">
              {coverage.fullyPaid.map(p => (
                <div key={p.id} className="flex justify-between text-xs">
                  <span className="text-tg-hint">Месяц #{p.payment_num}</span>
                  <span style={{ color: '#34C759' }}>закроется ✓</span>
                </div>
              ))}
              {coverage.partial && (
                <div className="flex justify-between text-xs">
                  <span className="text-tg-hint">Месяц #{coverage.partial.p.payment_num} — частично</span>
                  <div className="text-right">
                    <span style={{ color: '#F97316' }}>{formatMoney(coverage.partial.paid)} из {formatMoney(coverage.partial.p.needed)}</span>
                    <p className="text-tg-hint">остаток {formatMoney(coverage.partial.left)}</p>
                  </div>
                </div>
              )}
              {coverage.fullyPaid.length === 0 && !coverage.partial && (
                <p className="text-xs" style={{ color: '#D97757' }}>Сумма меньше необходимой ({formatMoney(stillNeeded)})</p>
              )}
              {coverage.leftover > 0.5 && (
                <p className="text-xs" style={{ color: '#34C759' }}>
                  Переплата: {formatMoney(coverage.leftover)} — перейдёт на следующий месяц
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Receipt photo ── */}
      {receiptUrl && (
        <div className="card mb-3">
          <p className="section-title mb-2">Фото чека</p>
          <div onClick={() => setImgOpen(true)} style={{ cursor: 'zoom-in' }}>
            <img src={receiptUrl} alt="чек" className="w-full rounded-xl object-cover" style={{ maxHeight: 220 }} />
          </div>
        </div>
      )}

      {/* ── Payment schedule ── */}
      <div className="card mb-4">
        <p className="section-title mb-3">График платежей</p>
        {sortedPayments.map(p => {
          const isCurrent = p.id === payment.id
          const color = p.status === 'paid' ? '#34C759' : p.status === 'overdue' ? '#D97757' : p.status === 'awaiting_confirmation' ? '#F97316' : '#8A8A96'
          return (
            <div
              key={p.id}
              className={`flex items-center gap-3 py-2.5 border-b border-tg-hint/10 last:border-0 rounded-lg ${isCurrent ? 'px-2' : ''}`}
              style={isCurrent ? { background: 'rgba(249,115,22,0.07)', margin: '0 -8px', padding: '10px 10px' } : {}}
            >
              <span style={{ width: 28, height: 28, borderRadius: '50%', background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color }}>
                  {p.status === 'paid' ? '✓' : p.payment_num}
                </span>
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-tg-text">{formatMoney(p.amount)}</span>
                  {isCurrent && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(249,115,22,0.15)', color: '#F97316', fontSize: 9, fontWeight: 600 }}>ТЕКУЩИЙ</span>}
                </div>
                <span className="text-xs text-tg-hint">{formatDate(p.due_date)}</span>
                {p.status === 'partially_paid' && (
                  <span className="text-xs" style={{ color: '#EAB308' }}>
                    оплачено {formatMoney(p.paid_amount)} · остаток {formatMoney(p.amount - p.paid_amount)}
                  </span>
                )}
              </div>
              <div className="text-right text-xs">
                <p style={{ color }}>{paymentStatusLabel(p.status)}</p>
                {p.paid_at && <p className="text-tg-hint">{formatDate(p.paid_at)}</p>}
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Actions ── */}
      {payment.status === 'awaiting_confirmation' && (
        <>
          {/* Confirmed amount input */}
          {!showReject && (
            <div className="card mb-3">
              <p className="section-title mb-1">Фактически получено (USD)</p>
              <p className="text-xs text-tg-hint mb-2">
                {clientClaim > 0
                  ? `Клиент заявил ${formatMoney(clientClaim)}. Введите фактически полученную сумму или оставьте пустым.`
                  : `Оставьте пустым — будет засчитана полная сумма остатка (${formatMoney(stillNeeded)})`
                }
              </p>
              <input
                className="input font-mono"
                inputMode="decimal"
                placeholder={clientClaim > 0 ? clientClaim.toFixed(2) : stillNeeded.toFixed(2)}
                value={confirmedAmountStr}
                onChange={e => setConfirmedAmountStr(e.target.value.replace(/[^0-9.,]/g, ''))}
              />
              {confirmedAmount !== undefined && (
                <div className="mt-2 text-xs">
                  {confirmedAmount >= stillNeeded
                    ? <span style={{ color: '#34C759' }}>
                        ✓ Месяц закрывается
                        {confirmedAmount > stillNeeded
                          ? ` · переплата ${formatMoney(confirmedAmount - stillNeeded)} → след. месяц`
                          : ''}
                      </span>
                    : <span style={{ color: '#EAB308' }}>
                        ⚠ Частичная оплата — остаток {formatMoney(stillNeeded - confirmedAmount)}
                      </span>
                  }
                </div>
              )}
            </div>
          )}

          {!showReject ? (
            <div className="flex gap-3 mb-4">
              <button
                onClick={() => confirmMut.mutate()}
                disabled={confirmMut.isPending}
                className="flex-1 py-3 rounded-xl font-semibold text-sm disabled:opacity-40"
                style={{ background: 'rgba(111,184,154,0.12)', color: '#6FB89A' }}
              >
                {confirmMut.isPending ? '...' : '✓ Подтвердить'}
              </button>
              <button
                onClick={() => setShowReject(true)}
                className="flex-1 py-3 rounded-xl font-semibold text-sm"
                style={{ background: 'rgba(217,119,87,0.10)', color: '#D97757' }}
              >
                ✕ Отклонить
              </button>
            </div>
          ) : (
            <div className="card mb-4">
              <p className="section-title mb-2">Причина отклонения</p>
              <textarea
                className="input resize-none mb-3"
                rows={3}
                placeholder="Необязательно"
                value={rejectNote}
                onChange={e => setRejectNote(e.target.value)}
              />
              <div className="flex gap-3">
                <button
                  onClick={() => rejectMut.mutate()}
                  disabled={rejectMut.isPending}
                  className="flex-1 py-3 rounded-xl font-semibold text-sm disabled:opacity-40"
                  style={{ background: 'rgba(217,119,87,0.10)', color: '#D97757' }}
                >
                  {rejectMut.isPending ? '...' : 'Отклонить'}
                </button>
                <button onClick={() => setShowReject(false)} className="btn-secondary flex-1 py-3">Отмена</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Fullscreen image overlay ── */}
      {imgOpen && receiptUrl && (
        <div
          onClick={() => setImgOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <img src={receiptUrl} alt="чек" style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 12, objectFit: 'contain' }} />
        </div>
      )}
    </Layout>
  )
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-tg-hint">{label}</span>
      <span className={highlight ? 'font-bold text-tg-btn' : 'text-tg-text'}>{value}</span>
    </div>
  )
}
